import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cx from "classnames";
import styles from "./storyLinePlan.module.scss";
import { usePromptEditorContext } from "src/components/app/contexts/promptEditorContext";

type TokenColor = "orange" | "purple" | "teal" | "blue" | "pink-bold";

interface Token {
  text: string;
  color: TokenColor;
}

const COLOR_MAP: Record<TokenColor, string> = {
  orange:      "#FFAC60",
  purple:      "#C478FF",
  teal:        "#1AC69C",
  blue:        "#3FA4FF",
  "pink-bold": "#FF32C6",
};

const FONT_WEIGHT_MAP: Record<TokenColor, number> = {
  orange:      400,
  purple:      400,
  teal:        400,
  blue:        400,
  "pink-bold": 600,
};

const TOKENS: Token[] = [
  { text: "// Type some code ->\n",               color: "orange" },
  { text: "console",                               color: "blue" },
  { text: ".",                                     color: "purple" },
  { text: "log ",                                  color: "blue" },
  { text: '"oO08 iIlL1 g9qCGQ ~-+=>"',            color: "orange" },
  { text: ";\n",                                   color: "purple" },
  { text: "// â é ù ï ø ç Ã Ē Æ œ\n",            color: "orange" },
  { text: "function ",                             color: "pink-bold" },
  { text: "updateGutters",                         color: "blue" },
  { text: "(",                                     color: "purple" },
  { text: "cm",                                    color: "orange" },
  { text: ") {\n",                                 color: "purple" },
  { text: "    var ",                              color: "pink-bold" },
  { text: "gutters",                               color: "purple" },
  { text: " = cm.",                                color: "purple" },
  { text: "display",                               color: "blue" },
  { text: ".",                                     color: "purple" },
  { text: "gutters",                               color: "purple" },
  { text: ",\n",                                   color: "purple" },
  { text: "        __specs",                       color: "purple" },
  { text: " = cm.",                                color: "purple" },
  { text: "options",                               color: "teal" },
  { text: ".",                                     color: "purple" },
  { text: "gutters",                               color: "purple" },
  { text: ";\n",                                   color: "purple" },
  { text: "    ",                                  color: "purple" },
  { text: "removeChildren",                        color: "teal" },
  { text: "(",                                     color: "purple" },
  { text: "gutters",                               color: "purple" },
  { text: ");\n",                                  color: "purple" },
  { text: "    ",                                  color: "purple" },
  { text: "for ",                                  color: "pink-bold" },
  { text: "(",                                     color: "purple" },
  { text: "var ",                                  color: "pink-bold" },
  { text: "i",                                     color: "purple" },
  { text: " = 0; i < specs.",                      color: "purple" },
  { text: "length",                                color: "blue" },
  { text: "; ++i) {\n",                            color: "purple" },
  { text: "        ",                              color: "purple" },
  { text: "var ",                                  color: "pink-bold" },
  { text: "gutterClass",                           color: "purple" },
  { text: " = __specs[i];\n",                      color: "purple" },
  { text: "        ",                              color: "purple" },
  { text: "var ",                                  color: "pink-bold" },
  { text: "gElt",                                  color: "purple" },
  { text: " = gutters.",                           color: "purple" },
  { text: "appendChild",                           color: "teal" },
  { text: "(\n            elt(\n",                 color: "purple" },
  { text: '                "div"',                 color: "orange" },
  { text: ", null,\n",                             color: "purple" },
  { text: '                "CodeMirror-gutter "',  color: "orange" },
  { text: " + gutterClass\n            )\n        );\n", color: "purple" },
  { text: "        ",                              color: "purple" },
  { text: "if ",                                   color: "pink-bold" },
  { text: "(gutterClass == ",                      color: "purple" },
  { text: '"CodeMirror-linenumbers"',              color: "orange" },
  { text: ") {\n",                                 color: "purple" },
  { text: "            cm.",                       color: "purple" },
  { text: "display",                               color: "blue" },
  { text: ".lineGutter = gElt;\n",                 color: "purple" },
  { text: "            gElt.",                     color: "purple" },
  { text: "style",                                 color: "blue" },
  { text: ".width = (cm.",                         color: "purple" },
  { text: "display",                               color: "blue" },
  { text: ".lineNumWidth || 1) + ",               color: "purple" },
  { text: '"px"',                                  color: "orange" },
  { text: ";\n        }\n    }\n",                 color: "purple" },
  { text: "    gutters.",                          color: "purple" },
  { text: "style",                                 color: "blue" },
  { text: ".display = i ? ",                       color: "purple" },
  { text: '""',                                    color: "orange" },
  { text: " : ",                                   color: "purple" },
  { text: '"none"',                                color: "orange" },
  { text: ";\n",                                   color: "purple" },
  { text: "    ",                                  color: "purple" },
  { text: "updateGutterSpace",                     color: "teal" },
  { text: "(cm);\n",                               color: "purple" },
  { text: "    return ",                           color: "purple" },
  { text: "false",                                 color: "purple" },
  { text: ";\n}\n\n",                              color: "purple" },
];

function CodeScrollLoader() {
  useEffect(() => {
    const id = "csl-keyframes";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;600&display=swap');
      @keyframes cslScrollUp {
        0%   { transform: translateY(0); }
        100% { transform: translateY(-50%); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const renderTokens = () =>
    [0, 1].flatMap((repeat) =>
      TOKENS.map((token, i) => (
        <span
          key={`${repeat}-${i}`}
          style={{
            color:               COLOR_MAP[token.color],
            fontWeight:          FONT_WEIGHT_MAP[token.color],
            opacity:             0.5,
            fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
          }}
        >
          {token.text}
        </span>
      ))
    );

  return (
    <div className={styles.codeLoader}>
      <div className={styles.codeLoaderFadeTop} />
      <div className={styles.codeLoaderScroll}>{renderTokens()}</div>
      <div className={styles.codeLoaderFadeBottom} />
    </div>
  );
}

const STATIC_THOUGHTS = [
  `Rome wasn't built in a day. Your page will be. Almost there.
Good things come to those who wait.
Building your page brick by brick`,
  `The web's first page, in 1991, was plain grey text. Yours won't be.
People read headlines five times more than the body copy. So we get yours right.`,
];
interface StoryLinePlanProps {
  thinkingLabel?: string;
}

export const StoryLinePlan: React.FC<StoryLinePlanProps> = ({
  thinkingLabel = "thinking for",
}) => {
  const navigate      = useNavigate();
  const location      = useLocation();
  const locationState = location.state as {
    prompt?:      string;
    workspaceId?: string;
    listId?:      string | null;
  } | null;
  const [staticThought] = useState(
    () => STATIC_THOUGHTS[Math.floor(Math.random() * STATIC_THOUGHTS.length)]
  );

  const prompt      = locationState?.prompt      ?? "";
  const workspaceId = locationState?.workspaceId ?? "";
  const listId      = locationState?.listId      ?? null;

  const { isLoading } = usePromptEditorContext();

  const [elapsedSecs, setElapsedSecs] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setElapsedSecs(0);
    timerRef.current = setInterval(() => {
      setElapsedSecs((s) => s + 1);
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [isLoading]);

  return (
    <aside className={styles.sideMenu}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <i
            className={cx("paperflite-arrow-up", styles.backArrow)}
            onClick={() =>
              navigate(
                `/experiences/${workspaceId}/storyboards${
                  listId ? `/folder/${listId}` : ""
                }`,
                { state: { prompt } },
              )
            }
          />
          Plan your storyline
        </h1>
      </div>

      <div className={styles.promptBubble}>
        <div className={styles.promptWrap}>
          <p className={styles.promptText}>
            {prompt || "Generating your storyboard…"}
          </p>
        </div>
      </div>

      <div
        className={cx(styles.responseCard, {
          [styles.responseCardLoading]: isLoading,
        })}
      >
        <div className={styles.cardHeader}>
          <img className={styles.seekGif} src="/images/seek.gif" alt="Seek" />
          <span className={styles.cardHeaderLabel}>
            {isLoading ? `Thinking for ${elapsedSecs}s` : "Meanwhile..."}
          </span>
        </div>

        <div className={cx(styles.cardBody, styles.cardBodyLoading)}>
          <div className={styles.cardContent}>
            {isLoading ? (
              <div className={styles.loaderWrap}>
                <CodeScrollLoader />
              </div>
            ) : (
              <div className={styles.thoughtWrap}>
                <p className={styles.thoughtText}>{staticThought}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};